<script>
import Multiselect from "vue-multiselect";
import { BSpinner } from "bootstrap-vue";

/**
 * Searchable, cursor-paginated select built on top of vue-multiselect.
 * `fetchOptions` must resolve an axios-style response whose `data` is
 * `{ data: [...], next_cursor }` (Laravel's cursorPaginate shape).
 */
export default {
    name: "AppAsyncMultiselect",

    components: { Multiselect, BSpinner },

    props: {
        value: { type: [Array, Object], default: null },
        fetchOptions: { type: Function, required: true },
        trackBy: { type: String, default: "id" },
        label: { type: String, default: "name" },
        multiple: { type: Boolean, default: false },
        placeholder: { type: String, default: "Search..." },
        disabled: { type: Boolean, default: false },
        perPage: { type: Number, default: 20 },
    },

    data() {
        return {
            options: [],
            searchTerm: "",
            cursor: null,
            hasMore: true,
            loading: false,
            loadingMore: false,
            error: false,
            searchTimer: null,
            scrollEl: null,
        };
    },

    methods: {
        async loadPage(isLoadMore) {
            if (this.loading || this.loadingMore) return;
            if (isLoadMore) this.loadingMore = true;
            else this.loading = true;
            this.error = false;

            try {
                const response = await this.fetchOptions({
                    search: this.searchTerm || null,
                    cursor: isLoadMore ? this.cursor : null,
                    per_page: this.perPage,
                });
                const body = response?.data ?? {};
                const items = body.data ?? [];

                this.options = isLoadMore ? [...this.options, ...items] : items;
                this.cursor = body.next_cursor ?? null;
                this.hasMore = !!this.cursor;
            } catch (error) {
                console.warn(error);
                this.error = true;
                if (!isLoadMore) this.options = [];
            }

            this.loading = false;
            this.loadingMore = false;
        },

        onSearchChange(term) {
            this.searchTerm = term;
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.cursor = null;
                this.hasMore = true;
                this.loadPage(false);
            }, 400);
        },

        onOpen() {
            if (!this.options.length && !this.loading) this.loadPage(false);

            this.$nextTick(() => {
                this.scrollEl = this.$el.querySelector(
                    ".multiselect__content-wrapper"
                );
                if (this.scrollEl)
                    this.scrollEl.addEventListener("scroll", this.onScroll);
            });
        },

        onClose() {
            if (this.scrollEl)
                this.scrollEl.removeEventListener("scroll", this.onScroll);
            this.scrollEl = null;
        },

        onScroll(event) {
            if (this.loadingMore || !this.hasMore) return;
            const el = event.target;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                this.loadPage(true);
            }
        },
    },

    beforeDestroy() {
        clearTimeout(this.searchTimer);
        this.onClose();
    },
};
</script>

<template>
    <multiselect
        :value="value"
        @input="$emit('input', $event)"
        :options="options"
        :multiple="multiple"
        :track-by="trackBy"
        :label="label"
        :placeholder="placeholder"
        :disabled="disabled"
        :loading="loading"
        :internal-search="false"
        :close-on-select="!multiple"
        :clear-on-select="false"
        :preserve-search="true"
        @search-change="onSearchChange"
        @open="onOpen"
        @close="onClose"
    >
        <template #noResult>
            <span>{{
                error
                    ? "Something went wrong while searching. Please try again."
                    : "No results found."
            }}</span>
        </template>
        <template #noOptions>
            <span>{{
                error
                    ? "Something went wrong while loading options."
                    : "No options available."
            }}</span>
        </template>
        <template #afterList>
            <div v-if="loadingMore" class="text-center py-2">
                <b-spinner small variant="primary" label="Loading more..." />
            </div>
        </template>
    </multiselect>
</template>
